// Detailseite eines sonstigen Kontakts (/admin/kontakte/kontakt/:id) im Widget-Stil (Handoff Sonstige Kontakte §2, Screen 05).
// Eigene Seite; die Sidebar wechselt in den Kontaktmodus (KontaktModus). Mobil (< 900 px): Zurück + Segment.
// Bearbeiten als Glas-Sheet, Löschen als Dialog (Handoff §3).
import { useCallback, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, CaretLeft, WarningCircle } from '@phosphor-icons/react';
import { KONTAKT_TAB_META, tabZaehler } from '../../../../components/immobilien/kontaktTabs';
import { GlasTooltipSchicht } from '../../../../components/ui/GlasTooltip';
import { Segment } from '../../../../components/ui/Segment';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { kontaktTab, kontaktTabPfad, meldeKontaktGeaendert, type KontaktTab } from '../../../../lib/kontakte/detailTab';
import { useSonstigerKontakt } from '../../../../lib/kontakte/useSonstigerKontakt';
import { DetailHero, InfoKarten } from './DetailKopf';
import { KontaktFormularSheet } from './KontaktFormularSheet';
import { KontaktLoeschenDialog } from './KontaktLoeschenDialog';
import { SonstigeAktivitaet } from './SonstigeAktivitaet';
import { SonstigeNotizen } from './SonstigeNotizen';
import { SonstigeUebersicht } from './SonstigeUebersicht';
import { SonstigeVerknuepft } from './SonstigeVerknuepft';
import { BriefVerlauf } from '../../dokumente/post/BriefVerlauf';

function ZurueckKnopf({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} data-tip="Zurück – Zur Liste der sonstigen Kontakte"
      className="sk-nav flex cursor-pointer items-center self-start"
      style={{ height: 44, padding: '0 14px 0 10px', borderRadius: 22, border: 'none', gap: 5, background: 'var(--fill2)', fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>
      <CaretLeft weight="bold" size={12} />
      Alle Kontakte
    </button>
  );
}

export default function SonstigeDetailSeite() {
  const { id = '' } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { mobil } = useMobilModus();
  const [offen, setOffen] = useState<'bearbeiten' | 'loeschen' | null>(null);
  const { zustand, anzahl } = useSonstigerKontakt(id, mobil);
  const tab = kontaktTab(params.get('tab'));
  const zurListe = useCallback(() => navigate('/admin/kontakte'), [navigate]);
  const zeigeTab = (t: KontaktTab) => navigate(kontaktTabPfad(id, t), { replace: true });
  const zumObjekt = useCallback((oid: string) => navigate('/admin/objekte/' + oid), [navigate]);

  // wie bisher: nach dem Speichern schließen und neu laden; nach dem Löschen zur Liste
  const schliessen = useCallback(() => setOffen(null), []);
  const gespeichert = () => { setOffen(null); meldeKontaktGeaendert(); };
  const geloescht = () => { setOffen(null); zurListe(); };

  if (zustand.status === 'laedt') {
    return (
      <div aria-busy="true" aria-label="Kontakt wird geladen" className="sk-flaeche flex flex-col" style={{ gap: 14, paddingTop: mobil ? 0 : 40 }}>
        {mobil && <ZurueckKnopf onClick={zurListe} />}
        <div className="animate-pulse" style={{ height: 120, borderRadius: 28, background: 'var(--fill2)' }} />
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 }}>
          {[0, 1, 2].map(i => <div key={i} className="animate-pulse" style={{ height: 68, borderRadius: 22, background: 'var(--fill2)' }} />)}
        </div>
        <div className="animate-pulse" style={{ height: 260, borderRadius: 26, background: 'var(--fill2)' }} />
      </div>
    );
  }

  if (zustand.status !== 'ok') {
    return (
      <div className="sk-flaeche flex flex-col" style={{ gap: 14 }}>
        <div role="alert" className="sk-glas sk-rise flex flex-col items-center text-center" style={{ borderRadius: 28, padding: '48px 20px', gap: 12 }}>
          <WarningCircle weight="fill" size={36} color={zustand.status === 'fehler' ? 'var(--bad)' : 'var(--ink3)'} />
          <span style={{ fontSize: 16, fontWeight: 800 }}>{zustand.status === 'fehlt' ? 'Keine Daten gefunden.' : 'Kontakt konnte nicht geladen werden'}</span>
          {zustand.status === 'fehler' && <span style={{ fontSize: 13, color: 'var(--ink3)', overflowWrap: 'anywhere' }}>{zustand.meldung}</span>}
          <div className="flex flex-wrap justify-center" style={{ gap: 8 }}>
            <ZurueckKnopf onClick={zurListe} />
            {zustand.status === 'fehler' && (
              <button type="button" onClick={meldeKontaktGeaendert} className="sk-knopf flex cursor-pointer items-center"
                style={{ height: 44, padding: '0 16px', borderRadius: 22, border: 'none', gap: 6, fontSize: 13, fontWeight: 800, color: 'var(--acc-ink)', background: 'var(--acc-fill)' }}>
                <ArrowClockwise weight="bold" size={14} />Erneut versuchen
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { kopf, zeile } = zustand;

  return (
    // Desktop: oben rechts steht die Chat-Glocke (ImmobilienInhalt) – Hero darunter beginnen lassen
    <div className="sk-flaeche sk-rise flex flex-col" style={{ gap: 14, paddingTop: mobil ? 0 : 40 }}>
      {mobil && <ZurueckKnopf onClick={zurListe} />}

      <DetailHero
        id={id}
        kopf={kopf}
        zeile={zeile}
        onBearbeiten={() => setOffen('bearbeiten')}
        onLoeschen={() => setOffen('loeschen')}
      />

      <InfoKarten kopf={kopf} zeile={zeile} />

      {mobil && (
        <div className="immo-ohne-scrollbar overflow-x-auto">
          <Segment<KontaktTab>
            ariaLabel="Bereich"
            voll
            hoehe={44}
            wert={tab}
            onChange={zeigeTab}
            eintraege={KONTAKT_TAB_META.map(t => {
              const n = tabZaehler(t, anzahl);
              return { key: t.key, label: t.label, anzahl: n ? Number(n) : undefined };
            })}
          />
        </div>
      )}

      {tab === 'uebersicht' && <SonstigeUebersicht zeile={zeile} telefon={kopf.telefon} />}
      {tab === 'verknuepft' && <SonstigeVerknuepft id={id} onNavigateToObjekt={zumObjekt} />}
      {tab === 'aktivitaet' && <SonstigeAktivitaet id={id} onNavigateToObjekt={zumObjekt} />}
      {tab === 'notizen' && <SonstigeNotizen id={id} />}
      {tab === 'briefe' && <BriefVerlauf art="kontakt" id={id} name={kopf.anzeigename} />}

      {offen === 'bearbeiten' && <KontaktFormularSheet kontakt={zeile} onSchliessen={schliessen} onGespeichert={gespeichert} />}
      {offen === 'loeschen' && <KontaktLoeschenDialog kontaktId={id} name={kopf.anzeigename} onAbbrechen={schliessen} onGeloescht={geloescht} />}
      <GlasTooltipSchicht />
    </div>
  );
}
